import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  Info,
  Shield,
  Target,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import RadioGroup from '@ui/RadioGroup';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import {
  formatCurrency,
  formatIndianNumberShort,
} from '@utils/numberFormatter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const RetirementPlanner = () => {
  // Wizard State
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Retirement Planning State
  const [currentAge, setCurrentAge] = useState<number | null>(30);
  const [retirementAge, setRetirementAge] = useState<number | null>(60);
  const [lifeExpectancy, setLifeExpectancy] = useState<number | null>(80);
  const [targetMonthlyPension, setTargetMonthlyPension] = useState<
    number | null
  >(50000);
  const [currentSavings, setCurrentSavings] = useState<number | null>(500000);
  const [monthlySIP, setMonthlySIP] = useState<number | null>(10000);
  const [expenseInflationRate, setExpenseInflationRate] = useState<
    number | null
  >(6);
  const [preRetirementReturn, setPreRetirementReturn] = useState<number | null>(
    12
  );
  const [postRetirementReturn, setPostRetirementReturn] = useState<
    number | null
  >(8);
  const [healthcareBufferPercent, setHealthcareBufferPercent] = useState<
    number | null
  >(20);
  const [retirementStrategy, setRetirementStrategy] = useState<
    'conservative' | 'moderate' | 'aggressive'
  >('moderate');

  const [results, setResults] = useState({
    yearsToRetirement: 0,
    retirementYears: 0,
    inflationAdjustedPension: 0,
    requiredCorpus: 0,
    totalRequiredCorpus: 0,
    totalRequiredCorpusTodayValue: 0,
    currentSavingsMaturity: 0,
    additionalCorpusNeeded: 0,
    requiredMonthlySIP: 0,
    projectedCorpus: 0,
    corpusShortfall: 0,
    sustainableWithdrawal: 0,
    yearlyWithdrawals: [] as any[],
    chartData: { labels: [], datasets: [] } as any,
    simulationSurvives: false,
    corpusDepletionAge: null as number | null,
  });

  // Check if current step is completed
  const isStepCompleted = (step: number) => {
    switch (step) {
      case 1:
        return (
          currentAge !== null &&
          retirementAge !== null &&
          lifeExpectancy !== null
        );
      case 2:
        return targetMonthlyPension !== null && expenseInflationRate !== null;
      case 3:
        return currentSavings !== null && monthlySIP !== null;
      case 4:
        return (
          preRetirementReturn !== null &&
          postRetirementReturn !== null &&
          healthcareBufferPercent !== null
        );
      default:
        return false;
    }
  };

  // Navigate to next step
  const nextStep = () => {
    if (currentStep < 5 && isStepCompleted(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Navigate to previous step
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Custom handler for current age to ensure retirement age is always >= current age
  const handleCurrentAgeChange = (newCurrentAge: number | null) => {
    setCurrentAge(newCurrentAge);

    // If retirement age is less than new current age, adjust it
    if (newCurrentAge && retirementAge && retirementAge < newCurrentAge) {
      setRetirementAge(newCurrentAge);
    }
  };

  // MASTER CALCULATION FUNCTION - Single source of truth for ALL retirement data
  const calculateRetirement = useCallback(() => {
    if (
      !currentAge ||
      !retirementAge ||
      !lifeExpectancy ||
      !targetMonthlyPension ||
      !expenseInflationRate ||
      !preRetirementReturn ||
      !postRetirementReturn
    ) {
      return;
    }

    const yearsToRetirement = Math.max(0, retirementAge - currentAge);
    const retirementYears = Math.max(0, lifeExpectancy - retirementAge);

    // Handle edge cases
    if (yearsToRetirement < 0 || retirementYears <= 0) {
      // Invalid scenario - reset to default values
      setResults({
        yearsToRetirement: 0,
        retirementYears: 0,
        inflationAdjustedPension: 0,
        requiredCorpus: 0,
        totalRequiredCorpus: 0,
        totalRequiredCorpusTodayValue: 0,
        currentSavingsMaturity: currentSavings || 0,
        additionalCorpusNeeded: 0,
        requiredMonthlySIP: 0,
        projectedCorpus: currentSavings || 0,
        corpusShortfall: 0,
        sustainableWithdrawal: 0,
        yearlyWithdrawals: [],
        chartData: { labels: [], datasets: [] },
        simulationSurvives: false,
        corpusDepletionAge: null,
      });
      return;
    }

    // Adjust target pension for inflation
    const inflationAdjustedPension =
      targetMonthlyPension *
      Math.pow(1 + expenseInflationRate / 100, yearsToRetirement);

    // Calculate required corpus using withdrawal rate based on strategy
    // Annual pension needed
    const annualPensionNeeded = inflationAdjustedPension * 12;

    // Safe withdrawal rate based on retirement strategy (conservative: 4%, moderate: 5%, aggressive: 6%)
    const safeWithdrawalRate =
      retirementStrategy === 'conservative'
        ? 0.04
        : retirementStrategy === 'moderate'
          ? 0.05
          : 0.06;

    // Calculate required corpus based on withdrawal strategy
    // The withdrawal strategy determines the corpus multiplier:
    // - Conservative (4%): Need 25x annual withdrawal (more conservative)
    // - Moderate (5%): Need 20x annual withdrawal (balanced)
    // - Aggressive (6%): Need 16.67x annual withdrawal (more aggressive)

    // Use the safe withdrawal rate to calculate required corpus
    // Corpus = Annual Withdrawal / Safe Withdrawal Rate
    const requiredCorpus = annualPensionNeeded / safeWithdrawalRate;
    const totalRequiredCorpus = requiredCorpus;

    // Calculate today's value of the required corpus
    // Discount the future required corpus back to present value
    const totalRequiredCorpusTodayValue =
      totalRequiredCorpus /
      Math.pow(1 + expenseInflationRate / 100, yearsToRetirement);

    // Calculate current savings maturity value
    const currentSavingsMaturity =
      (currentSavings || 0) *
      Math.pow(1 + preRetirementReturn / 100, yearsToRetirement);

    // Handle immediate retirement (0 years to retirement)
    let currentSIPMaturity = 0;
    let requiredMonthlySIP = 0;
    let additionalCorpusNeeded = 0;

    if (yearsToRetirement > 0) {
      const monthlyRate = preRetirementReturn / 100 / 12;
      const totalMonths = yearsToRetirement * 12;

      // Calculate SIP maturity with current SIP FIRST
      if (monthlySIP && monthlySIP > 0) {
        currentSIPMaturity =
          monthlySIP *
          (((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
            (1 + monthlyRate));
      }

      // Calculate additional corpus needed AFTER considering current SIP
      additionalCorpusNeeded = Math.max(
        0,
        totalRequiredCorpus - currentSavingsMaturity - currentSIPMaturity
      );

      // Required SIP will be calculated after simulation based on survival
    } else {
      // Immediate retirement - no time to invest
      additionalCorpusNeeded = Math.max(
        0,
        totalRequiredCorpus - currentSavingsMaturity
      );
      requiredMonthlySIP = 0; // Can't invest if retiring immediately
    }

    const projectedCorpus = currentSavingsMaturity + currentSIPMaturity;

    // === SINGLE RETIREMENT SIMULATION - Used for both shortfall and chart ===

    const simulationResults = [];
    let simulationCorpus = projectedCorpus;
    let simulationYearWithdrawal = annualPensionNeeded;
    const postRetirementRate = postRetirementReturn / 100;
    const inflationRate = expenseInflationRate / 100;
    let simulationSurvives = true;
    let depletionAge = null;

    for (let year = 1; year <= retirementYears; year++) {
      const age = retirementAge + year;
      const yearReturn = simulationCorpus * postRetirementRate;

      // Increase withdrawal by inflation each year (except first year)
      if (year > 1) {
        simulationYearWithdrawal =
          simulationYearWithdrawal * (1 + inflationRate);
      }

      const availableAmount = simulationCorpus + yearReturn;
      const actualWithdrawal = Math.min(
        simulationYearWithdrawal,
        availableAmount
      );

      simulationCorpus = availableAmount - actualWithdrawal;

      // Store the simulation result for this year
      const startingCorpusThisYear =
        simulationCorpus + actualWithdrawal - yearReturn;

      simulationResults.push({
        age,
        year,
        startingCorpus: startingCorpusThisYear,
        yearReturn,
        targetWithdrawal: simulationYearWithdrawal,
        actualWithdrawal,
        endingCorpus: Math.max(0, simulationCorpus),
        isDepletion: simulationCorpus <= 0,
      });

      // Check for depletion: either corpus depleted OR cannot afford target withdrawal
      // Different strategies have different risk tolerances for minimum corpus:
      // - Conservative: Require higher safety margin (1.5M minimum)
      // - Moderate: Standard safety margin (1M minimum)
      // - Aggressive: Accept lower safety margin (500K minimum)
      const minimumCorpusThreshold =
        retirementStrategy === 'aggressive'
          ? 500000
          : retirementStrategy === 'moderate'
            ? 1000000
            : 1500000;

      const cannotAffordTargetWithdrawal =
        actualWithdrawal < simulationYearWithdrawal;
      const corpusDepletedToLowAmount =
        simulationCorpus <= minimumCorpusThreshold;

      if (cannotAffordTargetWithdrawal || corpusDepletedToLowAmount) {
        simulationSurvives = false;
        depletionAge = age;
        break;
      }
    }

    // Calculate shortfall based on simulation results
    const corpusShortfall = simulationSurvives
      ? 0
      : Math.max(0, totalRequiredCorpus - projectedCorpus);

    // Calculate required SIP based on simulation results
    if (yearsToRetirement > 0) {
      if (simulationSurvives) {
        // Current SIP is sufficient
        requiredMonthlySIP = monthlySIP || 0;
      } else {
        // Use simple calculation: total corpus needed from SIP / SIP factor
        const totalCorpusFromSIP = totalRequiredCorpus - currentSavingsMaturity;
        const monthlyRate = preRetirementReturn / 100 / 12;
        const totalMonths = yearsToRetirement * 12;
        const sipFactor =
          ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
          (1 + monthlyRate);
        requiredMonthlySIP = totalCorpusFromSIP / sipFactor;
      }
    }

    // Calculate sustainable withdrawal with current plan
    const sustainableWithdrawal = (projectedCorpus * safeWithdrawalRate) / 12;

    // Generate yearly withdrawal projections with inflation adjustment (using existing simulation)
    const yearlyWithdrawals = simulationResults.map(result => ({
      year: result.year,
      startingCorpus: result.startingCorpus,
      returns: result.yearReturn,
      withdrawal: result.actualWithdrawal,
      endingCorpus: result.endingCorpus,
      inflationAdjustedWithdrawal: result.targetWithdrawal,
      withdrawalShortfall: Math.max(
        0,
        result.targetWithdrawal - result.actualWithdrawal
      ),
    }));

    // === GENERATE CHART DATA ===
    const chartYears: number[] = [];
    const chartCorpusData: number[] = [];
    const chartWithdrawalData: number[] = [];

    // Accumulation phase
    for (let year = 0; year <= yearsToRetirement; year++) {
      const age = currentAge + year;
      chartYears.push(age);

      let corpus = 0;
      if (year === 0) {
        corpus = currentSavings || 0;
      } else {
        const currentSavingsGrowth =
          (currentSavings || 0) * Math.pow(1 + preRetirementReturn / 100, year);

        const monthsInvested = year * 12;
        const monthlyRate = preRetirementReturn / 100 / 12;
        let sipValue = 0;
        if (monthsInvested > 0 && monthlySIP) {
          sipValue =
            monthlySIP *
            (((Math.pow(1 + monthlyRate, monthsInvested) - 1) / monthlyRate) *
              (1 + monthlyRate));
        }
        corpus = currentSavingsGrowth + sipValue;
      }

      chartCorpusData.push(corpus);
      chartWithdrawalData.push(0);
    }

    // Retirement phase - use EXACT simulation results
    for (const simResult of simulationResults) {
      chartYears.push(simResult.age);
      chartCorpusData.push(simResult.endingCorpus);
      chartWithdrawalData.push(simResult.actualWithdrawal);
    }

    // Chart markers and datasets
    const retirementMarkerData = chartYears.map((age, index) => {
      return age === retirementAge ? chartCorpusData[index] : null;
    });

    const requiredCorpusData = chartYears.map(age => {
      return age === retirementAge ? totalRequiredCorpus : null;
    });

    const chartData = {
      labels: chartYears,
      datasets: [
        {
          label: 'Required corpus',
          data: requiredCorpusData,
          backgroundColor: '#FFD700',
          borderColor: '#000000',
          borderWidth: 2,
          pointBackgroundColor: '#FFD700',
          pointBorderColor: '#000000',
          pointBorderWidth: 2,
          pointRadius: chartYears.map((_, index) =>
            requiredCorpusData[index] ? 12 : 0
          ),
          pointHoverRadius: chartYears.map((_, index) =>
            requiredCorpusData[index] ? 15 : 0
          ),
          showLine: false,
          fill: false,
          pointStyle: 'triangle',
        },
        {
          label: 'Your projected corpus',
          data: chartCorpusData,
          borderColor: '#3B82F6',
          backgroundColor: '#3B82F620',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#3B82F6',
          pointBorderColor: '#3B82F6',
          pointRadius: 2,
          pointHoverRadius: 5,
        },
        {
          label: 'Annual withdrawal',
          data: chartWithdrawalData,
          borderColor: '#EF4444',
          backgroundColor: '#EF444420',
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#EF4444',
          pointBorderColor: '#EF4444',
          pointRadius: 1,
          pointHoverRadius: 4,
          borderDash: [5, 5],
        },
        {
          label: 'Retirement age',
          data: retirementMarkerData,
          backgroundColor: '#10B981',
          borderColor: '#065F46',
          borderWidth: 2,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#065F46',
          pointBorderWidth: 2,
          pointRadius: chartYears.map((_, index) =>
            retirementMarkerData[index] ? 12 : 0
          ),
          pointHoverRadius: chartYears.map((_, index) =>
            retirementMarkerData[index] ? 15 : 0
          ),
          showLine: false,
          fill: false,
          pointStyle: 'rect',
        },
      ],
    };

    setResults({
      yearsToRetirement,
      retirementYears,
      inflationAdjustedPension,
      requiredCorpus,
      totalRequiredCorpus,
      totalRequiredCorpusTodayValue,
      currentSavingsMaturity,
      additionalCorpusNeeded,
      requiredMonthlySIP,
      projectedCorpus,
      corpusShortfall,
      sustainableWithdrawal,
      yearlyWithdrawals,
      chartData,
      simulationSurvives,
      corpusDepletionAge: depletionAge,
    });
  }, [
    currentAge,
    retirementAge,
    lifeExpectancy,
    targetMonthlyPension,
    currentSavings,
    monthlySIP,
    expenseInflationRate,
    preRetirementReturn,
    postRetirementReturn,
    retirementStrategy,
  ]);

  // Generate chart data for retirement journey
  // Chart data is now generated within calculateRetirement - no separate function needed!

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        align: 'start' as const,
        labels: {
          padding: 20,
          boxWidth: 10,
          boxHeight: 10,
          font: {
            size: 11,
            weight: 500,
          },
          filter: function (legendItem: any, chartData: any) {
            // Only show legend items that have data points
            const dataset = chartData.datasets[legendItem.datasetIndex];
            return dataset.data.some((value: any) => value !== null);
          },
          generateLabels: function (chart: any) {
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);

            return labels;
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: COLORS.accent,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            const datasetLabel = context.dataset.label;

            // Special handling for marker datasets
            if (datasetLabel === 'Retirement age') {
              return `Retirement age: ${retirementAge} years`;
            }

            if (datasetLabel === 'Required corpus') {
              const value = `₹${context.parsed.y.toLocaleString('en-IN')}`;
              return `Required corpus target: ${value}`;
            }

            // Default formatting for other datasets
            const value = `₹${context.parsed.y.toLocaleString('en-IN')}`;
            return `${datasetLabel}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.smoke,
        },
        title: {
          display: true,
          text: 'Age',
          color: COLORS.charcoal,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
      y: {
        grid: {
          color: `${COLORS.smoke}20`,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.charcoal,
          callback: function (value: any) {
            return `₹${value.toLocaleString('en-IN')}`;
          },
        },
        title: {
          display: true,
          text: 'Amount (₹)',
          color: COLORS.charcoal,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  // Recalculate when inputs change
  useEffect(() => {
    calculateRetirement();
  }, [calculateRetirement]);

  return (
    <ToolLayout
      title={`Retirement planner | ${CONTACT_INFO.company.name}`}
      heroTitle="Retirement planner"
      description="Plan your retirement corpus and monthly pension with our comprehensive retirement calculator"
      canonicalUrl="https://niveshright.com/tools/retirement-planner"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.gold}20` }}
        >
          <Calendar size={32} style={{ color: COLORS.gold }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="retirement planner, retirement corpus calculator, pension planning, retirement investment, SIP for retirement"
      cta={
        <WhatsAppCTA
          title="Ready to secure your retirement?"
          description="Get personalized retirement planning advice and create a robust retirement investment strategy."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={`Hi ${CONTACT_INFO.contact_person.short_name}! I used your Retirement Planner with these details:

Current age: ${currentAge} years
Retirement age: ${retirementAge} years (${results.yearsToRetirement} years to go)
Current savings: ${formatCurrency(currentSavings || 0)}
Monthly SIP: ${formatCurrency(monthlySIP || 0)}
Target pension: ${formatCurrency(targetMonthlyPension || 0)}/month (today's value)
Life expectancy: ${lifeExpectancy} years

CALCULATION RESULTS:
• Required corpus: ${formatCurrency(results.totalRequiredCorpus)}
• Projected corpus: ${formatCurrency(results.projectedCorpus)}
• Inflation-adjusted pension: ${formatCurrency(results.inflationAdjustedPension)}/month${
            results.corpusShortfall > 0
              ? `
Shortfall: ${formatCurrency(results.corpusShortfall)}
Required SIP: ${formatCurrency(results.requiredMonthlySIP)}/month`
              : ''
          }

I need help creating a comprehensive retirement plan. Can you guide me on the best investment strategy?`}
          buttonText="Get retirement plan"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.accent}
        />
      }
    >
      {/* Progress Indicator */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-center px-4">
          {[1, 2, 3, 4, 5].map(step => (
            <div key={step} className="flex items-center">
              <div
                className={`w-6 h-6 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-300 ${
                  step < currentStep || (step === 5 && currentStep === 5)
                    ? 'bg-green-500 text-white'
                    : step === currentStep
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step < currentStep || (step === 5 && currentStep === 5)
                  ? '✓'
                  : step}
              </div>
              {step < 5 && (
                <div
                  className={`w-4 sm:w-12 h-0.5 sm:h-1 mx-1 sm:mx-2 transition-all duration-300 ${
                    step < currentStep || (currentStep === 5 && step === 4)
                      ? 'bg-green-500'
                      : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-3 sm:mt-4">
          <p className="text-xs sm:text-sm text-gray-600">
            Step {currentStep} of 5:{' '}
            {currentStep === 1
              ? 'Personal information'
              : currentStep === 2
                ? 'Retirement goals'
                : currentStep === 3
                  ? 'Current financial status'
                  : currentStep === 4
                    ? 'Investment assumptions'
                    : 'Your retirement plan'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <Calendar
                  size={32}
                  style={{ color: COLORS.accent }}
                  className="mr-3"
                />
                <h2
                  className="text-3xl font-bold"
                  style={{ color: COLORS.primary }}
                >
                  Tell us about yourself
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                Let's start with your current age and retirement timeline
              </p>
            </div>

            <NumberInputWithSlider
              label="Current age"
              value={currentAge}
              onChange={handleCurrentAgeChange}
              min={15}
              max={75}
              step={1}
              placeholder="30"
              minLabel="15 years"
              maxLabel="75 years"
              className="mb-6"
            />

            <NumberInputWithSlider
              label="Target retirement age"
              value={retirementAge}
              onChange={setRetirementAge}
              min={Math.max(15, currentAge || 15)}
              max={85}
              step={1}
              placeholder="60"
              minLabel={`${Math.max(15, currentAge || 15)} years`}
              maxLabel="85 years"
              className="mb-6"
            />

            <NumberInputWithSlider
              label="Life expectancy"
              value={lifeExpectancy}
              onChange={setLifeExpectancy}
              min={65}
              max={110}
              step={1}
              placeholder="80"
              minLabel="65 years"
              maxLabel="110 years"
              className="mb-8"
            />

            <div className="flex justify-end">
              <motion.button
                onClick={nextStep}
                disabled={!isStepCompleted(1)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(1)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(1) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(1) ? { scale: 0.95 } : {}}
              >
                Continue →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Retirement Goals */}
        {currentStep === 2 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <Target
                  size={32}
                  style={{ color: COLORS.gold }}
                  className="mr-3"
                />
                <h2
                  className="text-3xl font-bold"
                  style={{ color: COLORS.primary }}
                >
                  What's your retirement goal?
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                Define your target pension and inflation expectations
              </p>
            </div>

            <NumberInputWithSlider
              label="Target monthly pension (today's value)"
              value={targetMonthlyPension}
              onChange={setTargetMonthlyPension}
              min={1000}
              max={10000000}
              step={5000}
              placeholder="50000"
              prefix="₹"
              showTooltip={true}
              minLabel="₹1K"
              maxLabel={`₹${formatIndianNumberShort(10000000, false)}`}
              className="mb-6"
            />

            <NumberInputWithSlider
              label="Expected expense inflation rate (%)"
              value={expenseInflationRate}
              onChange={setExpenseInflationRate}
              min={1}
              max={25}
              step={0.5}
              placeholder="6"
              minLabel="1%"
              maxLabel="25%"
              className="mb-8"
            />

            <div className="flex justify-between">
              <motion.button
                onClick={prevStep}
                className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ← Back
              </motion.button>
              <motion.button
                onClick={nextStep}
                disabled={!isStepCompleted(2)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(2)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(2) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(2) ? { scale: 0.95 } : {}}
              >
                Continue →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Current Financial Status */}
        {currentStep === 3 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <BarChart3
                  size={32}
                  style={{ color: COLORS.sage }}
                  className="mr-3"
                />
                <h2
                  className="text-3xl font-bold"
                  style={{ color: COLORS.primary }}
                >
                  Your current finances
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                Tell us about your existing savings and investment capability
              </p>
            </div>

            <NumberInputWithSlider
              label="Current retirement savings"
              value={currentSavings}
              onChange={setCurrentSavings}
              min={0}
              max={10000000000}
              step={50000}
              placeholder="500000"
              prefix="₹"
              showTooltip={true}
              minLabel="₹0"
              maxLabel={`₹${formatIndianNumberShort(10000000000, false)}`}
              className="mb-6"
            />

            <NumberInputWithSlider
              label="Monthly SIP capability"
              value={monthlySIP}
              onChange={setMonthlySIP}
              min={0}
              max={10000000}
              step={1000}
              placeholder="10000"
              prefix="₹"
              showTooltip={true}
              minLabel="₹0"
              maxLabel={`₹${formatIndianNumberShort(10000000, false)}`}
              className="mb-6"
              disabled={currentAge === retirementAge}
            />

            {currentAge === retirementAge && (
              <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-amber-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-amber-800">
                      <strong>SIP Not Applicable</strong> - Since you're
                      retiring immediately (current age = retirement age),
                      there's no time to invest through SIP. Focus on optimizing
                      your current savings instead.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between mt-8">
              <motion.button
                onClick={prevStep}
                className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ← Back
              </motion.button>
              <motion.button
                onClick={nextStep}
                disabled={!isStepCompleted(3)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(3)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(3) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(3) ? { scale: 0.95 } : {}}
              >
                Continue →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Investment Assumptions */}
        {currentStep === 4 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <Shield
                  size={32}
                  style={{ color: COLORS.secondary }}
                  className="mr-3"
                />
                <h2
                  className="text-3xl font-bold"
                  style={{ color: COLORS.primary }}
                >
                  Investment strategy
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                Set your expected returns and retirement strategy
              </p>
            </div>

            <NumberInputWithSlider
              label="Pre-retirement expected return %"
              value={preRetirementReturn}
              onChange={setPreRetirementReturn}
              min={1}
              max={100}
              step={0.5}
              placeholder="12"
              minLabel="1%"
              maxLabel="100%"
              className="mb-6"
            />

            <NumberInputWithSlider
              label="Post-retirement expected return %"
              value={postRetirementReturn}
              onChange={setPostRetirementReturn}
              min={1}
              max={100}
              step={0.5}
              placeholder="8"
              minLabel="1%"
              maxLabel="100%"
              className="mb-6"
            />

            <RadioGroup
              label="Retirement strategy"
              options={[
                {
                  value: 'conservative',
                  label: 'Conservative',
                  description: '4% withdrawal rate',
                },
                {
                  value: 'moderate',
                  label: 'Moderate',
                  description: '5% withdrawal rate',
                },
                {
                  value: 'aggressive',
                  label: 'Aggressive',
                  description: '6% withdrawal rate',
                },
              ]}
              value={retirementStrategy}
              onChange={value =>
                setRetirementStrategy(
                  value as 'conservative' | 'moderate' | 'aggressive'
                )
              }
              className="mb-6"
            />

            <div className="flex justify-between">
              <motion.button
                onClick={prevStep}
                className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ← Back
              </motion.button>
              <motion.button
                onClick={() => {
                  nextStep();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={!isStepCompleted(4)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(4)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(4) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(4) ? { scale: 0.95 } : {}}
              >
                View results →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 5: Results */}
        {currentStep === 5 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Results Section */}
            <div className="space-y-6">
              {/* Input Summary - Redesigned */}
              <motion.div
                className="bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.2 }}
              >
                {/* Header */}
                <div className="px-8 sm:px-12 py-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                      <Calendar size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Your planning inputs
                      </h3>
                      <p className="text-sm text-gray-500">
                        Review your retirement planning assumptions
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="px-4 sm:px-8 pb-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4">
                    {/* Personal Timeline */}
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                      <h4 className="font-bold text-gray-600 text-base text-center mb-4">
                        Personal timeline
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-2 border-b border-gray-50">
                          <span className="text-xs font-medium text-gray-600">
                            Current age
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            {currentAge}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-50">
                          <span className="text-xs font-medium text-gray-600">
                            Retirement age
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            {retirementAge}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-xs font-medium text-gray-600">
                            Life expectancy
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            {lifeExpectancy}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Retirement Goals */}
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                      <h4 className="font-bold text-gray-600 text-base text-center mb-4">
                        Retirement goals
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-2 border-b border-gray-50">
                          <span className="text-xs font-medium text-gray-600">
                            Target monthly pension
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            <LocalizedCurrency
                              value={targetMonthlyPension || 0}
                            />
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-50">
                          <span className="text-xs font-medium text-gray-600">
                            Inflation rate
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            {expenseInflationRate}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-50">
                          <span className="text-xs font-medium text-gray-600">
                            Withdrawal strategy
                          </span>
                          <span className="font-bold text-gray-900 text-xs capitalize">
                            {retirementStrategy}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Investment & Finances */}
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                      <h4 className="font-bold text-gray-600 text-base text-center mb-4">
                        Investment & finances
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-2 border-b border-gray-50">
                          <span className="text-xs font-medium text-gray-600">
                            Current savings
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            <LocalizedCurrency value={currentSavings || 0} />
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-50">
                          <span className="text-xs font-medium text-gray-600">
                            Monthly SIP
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            <LocalizedCurrency value={monthlySIP || 0} />
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-50">
                          <span className="text-xs font-medium text-gray-600">
                            Pre-retirement return
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            {preRetirementReturn}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-xs font-medium text-gray-600">
                            Post-retirement return
                          </span>
                          <span className="font-bold text-gray-900 text-xs">
                            {postRetirementReturn}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Main Results Table */}
              <motion.div
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.6 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <Target
                      size={24}
                      style={{ color: COLORS.accent }}
                      className="mr-3"
                    />
                    <div>
                      <h3
                        className="text-2xl font-semibold"
                        style={{ color: COLORS.primary }}
                      >
                        Retirement plan analysis
                      </h3>
                      <p className="text-sm text-gray-500">
                        Key financial projections for your retirement plan
                      </p>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <tbody className="divide-y divide-gray-100">
                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-700">
                          Timeline
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className="font-semibold text-lg"
                            style={{ color: COLORS.accent }}
                          >
                            {results.yearsToRetirement} years to retirement
                          </span>
                          <p className="text-sm text-gray-500">
                            {results.retirementYears} years of retirement (age{' '}
                            {retirementAge} to {lifeExpectancy})
                          </p>
                        </td>
                      </tr>

                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-700">
                          Monthly pension
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className="font-semibold text-lg"
                            style={{ color: COLORS.gold }}
                          >
                            <LocalizedCurrency
                              value={results.inflationAdjustedPension}
                            />
                          </span>
                          <p className="text-sm text-gray-500">
                            Inflation-adjusted from today's{' '}
                            <LocalizedCurrency
                              value={targetMonthlyPension || 0}
                            />
                          </p>
                        </td>
                      </tr>

                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-700">
                          Required corpus
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className="font-semibold text-lg"
                            style={{ color: COLORS.sage }}
                          >
                            <LocalizedCurrency
                              value={results.totalRequiredCorpus}
                            />
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            Today's value:{' '}
                            <LocalizedCurrency
                              value={results.totalRequiredCorpusTodayValue}
                            />
                          </p>
                        </td>
                      </tr>

                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-700">
                          Your projected corpus
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className="font-semibold text-lg"
                            style={{
                              color:
                                results.corpusShortfall > 0
                                  ? '#ef4444'
                                  : COLORS.accent,
                            }}
                          >
                            <LocalizedCurrency
                              value={results.projectedCorpus}
                            />
                          </span>
                          <p className="text-sm text-gray-500">
                            Current savings:{' '}
                            <LocalizedCurrency
                              value={results.currentSavingsMaturity}
                            />{' '}
                            + SIP growth:{' '}
                            <LocalizedCurrency
                              value={
                                results.projectedCorpus -
                                results.currentSavingsMaturity
                              }
                            />
                          </p>
                        </td>
                      </tr>

                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-700">
                          {results.corpusShortfall > 0 ? 'Shortfall' : 'Status'}
                        </td>
                        <td className="py-3 px-4">
                          {results.corpusShortfall > 0 ? (
                            <>
                              <span className="font-semibold text-lg text-red-600">
                                -{' '}
                                <LocalizedCurrency
                                  value={results.corpusShortfall}
                                />
                              </span>
                              <p className="text-sm text-gray-500">
                                Need to increase savings
                              </p>
                            </>
                          ) : (
                            <>
                              <span className="font-semibold text-lg text-green-600">
                                ✓ Goal achievable
                              </span>
                              <p className="text-sm text-gray-500">
                                Your plan will sustain the target pension
                              </p>
                            </>
                          )}
                        </td>
                      </tr>

                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-700">
                          Required monthly SIP
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`font-semibold text-lg ${results.corpusShortfall > 0 ? 'text-red-600' : 'text-green-600'}`}
                          >
                            <LocalizedCurrency
                              value={
                                results.corpusShortfall > 0
                                  ? results.requiredMonthlySIP
                                  : monthlySIP || 0
                              }
                            />
                          </span>
                          <p className="text-sm text-gray-500">
                            {results.corpusShortfall > 0
                              ? `Increase by `
                              : `Current `}
                            {results.corpusShortfall > 0 && (
                              <LocalizedCurrency
                                value={
                                  results.requiredMonthlySIP - (monthlySIP || 0)
                                }
                              />
                            )}
                            {results.corpusShortfall > 0
                              ? ` from current `
                              : ``}
                            <LocalizedCurrency value={monthlySIP || 0} />
                            {results.corpusShortfall > 0
                              ? ``
                              : ` is sufficient`}
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* Growth Chart */}
              <motion.div
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.8 }}
              >
                <div className="flex items-center mb-6">
                  <BarChart3
                    size={24}
                    style={{ color: COLORS.gold }}
                    className="mr-3"
                  />
                  <h3
                    className="text-2xl font-semibold"
                    style={{ color: COLORS.primary }}
                  >
                    Retirement journey projection
                  </h3>
                </div>

                <div className="h-80">
                  <Line data={results.chartData} options={chartOptions} />
                </div>
              </motion.div>

              {/* Status Alert - Single Consolidated Alert */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 1.0 }}
              >
                {results.corpusShortfall > 0 ? (
                  <div className="p-6 bg-red-50 border-l-4 border-red-400 rounded-r-xl">
                    <div className="flex items-start">
                      <AlertTriangle
                        size={24}
                        className="text-red-600 mr-3 mt-1 flex-shrink-0"
                      />
                      <div>
                        <h4 className="text-lg font-semibold text-red-800 mb-2">
                          Action required
                        </h4>
                        <p className="text-red-700 mb-3">
                          Your current plan falls short by{' '}
                          <strong>
                            <LocalizedCurrency
                              value={results.corpusShortfall}
                            />
                          </strong>
                          . Here are your options:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white p-4 rounded-lg border border-red-200">
                            <h5 className="font-semibold text-red-800 mb-2">
                              💰 Increase SIP
                            </h5>
                            <p className="text-sm text-red-700">
                              Monthly SIP:{' '}
                              <strong>
                                <LocalizedCurrency value={monthlySIP || 0} /> →{' '}
                                <LocalizedCurrency
                                  value={results.requiredMonthlySIP}
                                />
                              </strong>
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded-lg border border-red-200">
                            <h5 className="font-semibold text-red-800 mb-2">
                              📅 Delay retirement
                            </h5>
                            <p className="text-sm text-red-700">
                              Work 2-3 years longer to build more corpus
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded-lg border border-red-200">
                            <h5 className="font-semibold text-red-800 mb-2">
                              📉 Reduce target
                            </h5>
                            <p className="text-sm text-red-700">
                              Lower monthly pension expectation
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded-lg border border-red-200">
                            <h5 className="font-semibold text-red-800 mb-2">
                              🎯 Change strategy
                            </h5>
                            <p className="text-sm text-red-700">
                              Switch to aggressive (6%) withdrawal rate
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-green-50 border-l-4 border-green-400 rounded-r-xl">
                    <div className="flex items-start">
                      <Shield
                        size={24}
                        className="text-green-600 mr-3 mt-1 flex-shrink-0"
                      />
                      <div>
                        <h4 className="text-lg font-semibold text-green-800 mb-2">
                          Retirement goal is achievable! 🎉
                        </h4>
                        <p className="text-green-700 mb-3">
                          Your current plan will build{' '}
                          <strong>
                            <LocalizedCurrency
                              value={results.projectedCorpus}
                            />
                          </strong>
                          , which our simulation confirms will sustain your
                          target monthly pension throughout retirement.
                        </p>
                        <div className="bg-white p-4 rounded-lg border border-green-200">
                          <h5 className="font-semibold text-green-800 mb-2">
                            ✅ What this means:
                          </h5>
                          <ul className="text-sm text-green-700 space-y-1">
                            <li>• Your retirement income is secured</li>
                            <li>• You have buffer for unexpected expenses</li>
                            <li>
                              • You may consider increasing your target
                              lifestyle or retiring earlier
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  onClick={() => {
                    setCurrentStep(4);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ← Modify plan
                </motion.button>
                <motion.button
                  onClick={() => {
                    // Reset all values to defaults
                    setCurrentAge(30);
                    setRetirementAge(60);
                    setLifeExpectancy(80);
                    setTargetMonthlyPension(50000);
                    setCurrentSavings(500000);
                    setMonthlySIP(10000);
                    setExpenseInflationRate(6);
                    setPreRetirementReturn(12);
                    setPostRetirementReturn(8);
                    setHealthcareBufferPercent(20);
                    setRetirementStrategy('moderate');
                    setCurrentStep(1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="px-8 py-3 rounded-xl font-semibold bg-blue-500 text-white hover:bg-blue-600 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Plan another retirement
                </motion.button>
              </div>

              {/* Disclaimer */}
              <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 shadow-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <Info size={24} className="text-amber-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-amber-800 mb-3">
                      Disclaimer
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="font-semibold text-amber-800 mb-2 text-sm">
                          Risk factors:
                        </p>
                        <ul className="space-y-1 text-sm text-amber-700">
                          <li>
                            • <strong>Returns are not guaranteed</strong>
                          </li>
                          <li>• Actual returns may vary significantly</li>
                          <li>• Market volatility can affect outcomes</li>
                          <li>• Healthcare costs may exceed estimates</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold text-amber-800 mb-2 text-sm">
                          Professional advice:
                        </p>
                        <ul className="space-y-1 text-sm text-amber-700">
                          <li>
                            •{' '}
                            <strong>
                              Consult a certified financial advisor
                            </strong>
                          </li>
                          <li>• Review your risk tolerance</li>
                          <li>• Consider inflation impact</li>
                          <li>• Monitor and adjust regularly</li>
                        </ul>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-amber-100 rounded-lg">
                      <p className="text-xs text-amber-800 font-medium">
                        <strong>This planner provides estimates only.</strong>{' '}
                        Your actual retirement journey may differ based on
                        market conditions, life expectancy changes, healthcare
                        costs, and economic factors.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Term Definitions */}
      <motion.section
        className="max-w-4xl mx-auto py-6 mt-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.4 }}
      >
        <div className="bg-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Key terms</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                term: 'Retirement corpus',
                definition:
                  'The total amount of money you need to accumulate by retirement to sustain your desired lifestyle. This acts as your financial foundation for post-retirement years.',
              },
              {
                term: 'Target monthly pension',
                definition:
                  "The monthly income you want during retirement in today's purchasing power. This should cover your expected lifestyle and expenses.",
              },
              {
                term: 'Inflation rate',
                definition:
                  'The annual rate at which prices of goods and services increase. Your retirement corpus must grow faster than inflation to maintain purchasing power.',
              },
              {
                term: 'Withdrawal rate strategy',
                definition:
                  "This determines how much corpus you need to build for the same retirement lifestyle. Example: If you need ₹12 lakh/year - Conservative (4%) requires ₹3 crore corpus, Moderate (5%) requires ₹2.4 crore corpus, Aggressive (6%) requires ₹2 crore corpus. Conservative is safest but needs more savings upfront. Aggressive needs less savings but has higher risk if markets don't perform well.",
              },
              {
                term: 'Pre-retirement return',
                definition:
                  'Expected annual returns on your investments before retirement. Typically higher due to equity-heavy portfolios during accumulation phase.',
              },
              {
                term: 'Post-retirement return',
                definition:
                  'Expected annual returns during retirement. Usually lower as portfolios shift to safer, income-generating assets like bonds and dividend stocks.',
              },
              {
                term: 'SIP (Systematic Investment Plan)',
                definition:
                  'Regular monthly investments in mutual funds. Helps build retirement corpus through disciplined investing and rupee cost averaging.',
              },
              {
                term: 'Corpus depletion',
                definition:
                  'When your retirement savings run out before your life expectancy. Proper planning ensures your money lasts throughout retirement.',
              },
            ].map((item, index) => (
              <div key={index} className="mb-4">
                <h3 className="text-md font-semibold text-gray-900 mb-2">
                  {item.term}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.definition}
                </p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Understanding Withdrawal Strategies */}
      <motion.section
        className="max-w-4xl mx-auto py-6 mt-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.6 }}
      >
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border border-blue-100">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Understanding withdrawal strategies
            </h3>
            <p className="text-gray-600 text-base">
              Your withdrawal strategy determines how much money you need to
              save for retirement. Here's a simple breakdown:
            </p>
          </div>

          <div className="space-y-6">
            {/* Conservative Strategy */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-green-500 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Shield size={24} className="text-green-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Conservative (4% withdrawal rate)
                  </h4>
                  <p className="text-gray-700 mb-3">
                    <strong>Best for:</strong> Risk-averse retirees, those with
                    longer life expectancy, or anyone who wants maximum safety.
                  </p>
                  <div className="bg-green-50 p-4 rounded-lg mb-3">
                    <p className="text-sm text-gray-800 font-semibold mb-2">
                      Real-world example:
                    </p>
                    <p className="text-sm text-gray-700">
                      Ravi, 60, wants ₹1 lakh/month (₹12 lakh/year).
                      <br />
                      <strong>Corpus needed: ₹3 crore</strong>
                      <br />
                      <span className="text-xs text-gray-600">
                        This gives him ₹12L per year throughout retirement
                      </span>
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <strong className="text-green-700">✓ Pros:</strong>
                      <ul className="text-gray-600 mt-1 space-y-1">
                        <li>• Highest safety margin</li>
                        <li>• Corpus lasts longest</li>
                        <li>• Can handle market crashes</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-red-700">✗ Cons:</strong>
                      <ul className="text-gray-600 mt-1 space-y-1">
                        <li>• Need to save more upfront</li>
                        <li>• Takes longer to accumulate</li>
                        <li>• Higher SIP required</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Moderate Strategy */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-blue-500 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Target size={24} className="text-blue-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Moderate (5% withdrawal rate)
                  </h4>
                  <p className="text-gray-700 mb-3">
                    <strong>Best for:</strong> Most retirees seeking balance
                    between safety and achievability. The "sweet spot" for many.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg mb-3">
                    <p className="text-sm text-gray-800 font-semibold mb-2">
                      Real-world example:
                    </p>
                    <p className="text-sm text-gray-700">
                      Priya, 58, wants ₹1 lakh/month (₹12 lakh/year).
                      <br />
                      <strong>Corpus needed: ₹2.4 crore</strong>
                      <br />
                      <span className="text-xs text-gray-600">
                        Balanced approach - good safety margin with achievable
                        target
                      </span>
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <strong className="text-green-700">✓ Pros:</strong>
                      <ul className="text-gray-600 mt-1 space-y-1">
                        <li>• Good safety margin</li>
                        <li>• More achievable target</li>
                        <li>• Balanced risk-reward</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-red-700">✗ Cons:</strong>
                      <ul className="text-gray-600 mt-1 space-y-1">
                        <li>• Less buffer than conservative</li>
                        <li>• Moderate market risk</li>
                        <li>• Needs discipline</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Aggressive Strategy */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-orange-500 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <AlertTriangle size={24} className="text-orange-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Aggressive (6% withdrawal rate)
                  </h4>
                  <p className="text-gray-700 mb-3">
                    <strong>Best for:</strong> Late starters, those with other
                    income sources, or shorter retirement horizons (10-15
                    years).
                  </p>
                  <div className="bg-orange-50 p-4 rounded-lg mb-3">
                    <p className="text-sm text-gray-800 font-semibold mb-2">
                      Real-world example:
                    </p>
                    <p className="text-sm text-gray-700">
                      Amit, 55, wants ₹1 lakh/month (₹12 lakh/year).
                      <br />
                      <strong>Corpus needed: ₹2 crore</strong>
                      <br />
                      <span className="text-xs text-gray-600">
                        Lower savings needed, but depends on markets staying
                        strong
                      </span>
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <strong className="text-green-700">✓ Pros:</strong>
                      <ul className="text-gray-600 mt-1 space-y-1">
                        <li>• Lowest corpus needed</li>
                        <li>• Easier to achieve</li>
                        <li>• Good for late starters</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-red-700">✗ Cons:</strong>
                      <ul className="text-gray-600 mt-1 space-y-1">
                        <li>• Higher depletion risk</li>
                        <li>• Vulnerable to market crashes</li>
                        <li>• Less safety cushion</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h4 className="text-lg font-bold text-gray-900 mb-4">
                Same lifestyle, different corpus needed
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Strategy
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        For ₹12L/year income
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        For ₹24L/year income
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Risk level
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="hover:bg-green-50">
                      <td className="py-3 px-4 font-medium text-gray-900">
                        Conservative
                      </td>
                      <td className="py-3 px-4 text-gray-700 font-bold">
                        ₹3 crore
                      </td>
                      <td className="py-3 px-4 text-gray-700 font-bold">
                        ₹6 crore
                      </td>
                      <td className="py-3 px-4 text-green-600 text-xs font-semibold">
                        Safest
                      </td>
                    </tr>
                    <tr className="hover:bg-blue-50">
                      <td className="py-3 px-4 font-medium text-gray-900">
                        Moderate
                      </td>
                      <td className="py-3 px-4 text-gray-700 font-bold">
                        ₹2.4 crore
                      </td>
                      <td className="py-3 px-4 text-gray-700 font-bold">
                        ₹4.8 crore
                      </td>
                      <td className="py-3 px-4 text-blue-600 text-xs font-semibold">
                        Balanced
                      </td>
                    </tr>
                    <tr className="hover:bg-orange-50">
                      <td className="py-3 px-4 font-medium text-gray-900">
                        Aggressive
                      </td>
                      <td className="py-3 px-4 text-gray-700 font-bold">
                        ₹2 crore
                      </td>
                      <td className="py-3 px-4 text-gray-700 font-bold">
                        ₹4 crore
                      </td>
                      <td className="py-3 px-4 text-orange-600 text-xs font-semibold">
                        Higher risk
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-800">
                  <strong>💡 The key point:</strong> All three strategies give
                  you the <strong>same retirement income</strong>. The
                  difference is how much you need to save upfront and your
                  safety margin if markets crash.
                </p>
              </div>
            </div>

            {/* Key Insight */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-start">
                <Info
                  size={24}
                  className="text-purple-600 mr-3 mt-1 flex-shrink-0"
                />
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    💡 Key insight
                  </h4>
                  <p className="text-gray-700 mb-2">
                    The withdrawal rate doesn't mean "you'll run out faster with
                    aggressive". Instead, it determines{' '}
                    <strong>how much you need to save initially</strong>.
                  </p>
                  <p className="text-gray-700">
                    Think of it like this: Conservative is building a bigger
                    fuel tank for the same journey. Aggressive is building a
                    smaller tank but requires excellent road conditions (stable
                    markets) throughout the journey.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </ToolLayout>
  );
};
